import * as THREE from 'three';
import { buildBone } from './bones.js';
import { buildShape, shapeDetailFor, type ShapeBudget, type ShapeDetail } from './shapes.js';
import { buildStudio } from './studio.js';
import { buildPoster, loadPosterPhoto, type PosterController } from './posters.js';
import {
  DEFAULT_RADIUS,
  framingScene,
  isPosterKind,
  posterFacing,
  framingSceneAt,
  modelScale,
  modelStatesAt,
  type FramingScene,
  type Lesson,
  type LessonObject,
  type ModelCatalogue,
  type ObjectState,
  type Step,
  type WorldCatalogue,
} from '@vrlearning/lesson-schema';
import * as T from './textures.js';
import { makeLabel } from './panel.js';
import type { SceneGeometry, Vec3 } from '../engine/view.js';
import type { Bounds } from '../engine/lock-anchor.js';

export type BuiltObject = {
  readonly id: string;
  readonly group: THREE.Group;
  /** The pickable body. Hotspot rays and focus dimming use this. */
  readonly body: THREE.Mesh;
  readonly label: THREE.Sprite;
  readonly radius: number;
  readonly spin: number;
  readonly parent?: string | undefined;
  readonly orbit?: number | undefined;
  /** Extra meshes that spin at their own rate, e.g. Earth's clouds. */
  readonly spinners: readonly { object: THREE.Object3D; rate: number }[];
  /**
   * Where a hotspot badge goes, in the object's own space, and how big.
   * Set by builders whose objects are small and close together (bones);
   * everything else keeps the planet rule in badges.ts.
   */
  readonly badge?: { at: THREE.Vector3; size: number } | undefined;
};

type BuildContext = {
  id: string;
  name: string;
  object: LessonObject;
  /** How finely shapes are drawn in this lesson; only the shape builder reads it. */
  shapeDetail?: ShapeDetail;
  /** The served lesson's library models (ADR-043); only the model builder reads it. */
  catalogue?: ModelCatalogue;
};
type ObjectBuilder = (ctx: BuildContext) => {
  body: THREE.Mesh;
  extras?: THREE.Object3D[];
  spinners?: { object: THREE.Object3D; rate: number }[];
  labelScale?: number;
  labelHeight?: number;
  /**
   * Where the name goes, beside the object rather than above it, and which
   * way it reads out from that point. For objects close together -- sixteen
   * bones in a column -- "above the top" puts every name in the same place.
   */
  nameplate?: { at: [number, number, number]; side: -1 | 0 | 1 };
  /**
   * How far a viewpoint stands back, when the builder knows better than the
   * lesson's `radius`: a shape measures itself from its parts.
   */
  radius?: number;
  /** Where the hotspot badge goes, in the object's space, and how big. */
  badge?: { at: [number, number, number]; size: number };
  /** A poster's photo, put on when it arrives (render/posters.ts). */
  poster?: PosterController;
};

/**
 * Builders are registered per `kind` namespace, not per full kind. That is what
 * lets a lesson introduce `planet:venus` without touching this file, while
 * `pano360:` or a new namespace stays an explicit, reviewed addition.
 */
const builders = new Map<string, ObjectBuilder>();

const sphere = (radius: number, detail = 1): THREE.SphereGeometry =>
  new THREE.SphereGeometry(radius, detail > 0 ? 96 : 48, detail > 0 ? 64 : 32);

// A skeleton's parts, built from primitives in bones.ts to keep this file
// about the registry rather than about anatomy.
builders.set('bone', buildBone);

// Anything else a lesson builds from parts (ADR-030): primitives, turned and
// extruded outlines, groups. One builder for every name, because the recipe
// is in the lesson rather than in here.
builders.set('shape', ({ object, shapeDetail }) => buildShape(object, shapeDetail));

// A framed photo (posters spec): the frame at once, the photo when it arrives.
builders.set('poster', ({ object }) => buildPoster(object));

builders.set('star', ({ object }) => {
  const radius = object.radius ?? DEFAULT_RADIUS.star;
  const body = new THREE.Mesh(sphere(radius), new THREE.MeshBasicMaterial({ map: T.sunTexture() }));
  const glow = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: T.glowSprite(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  glow.scale.setScalar(radius * 4.2);
  return { body, extras: [glow], labelHeight: radius * 1.35 + 0.2 };
});

builders.set('planet', ({ name, object }) => {
  const radius = object.radius ?? DEFAULT_RADIUS.planet;
  const extras: THREE.Object3D[] = [];
  const spinners: { object: THREE.Object3D; rate: number }[] = [];
  let body: THREE.Mesh;

  switch (name) {
    case 'earth': {
      body = new THREE.Mesh(
        sphere(radius),
        new THREE.MeshStandardMaterial({ map: T.earthTexture(), roughness: 0.75, metalness: 0.05 }),
      );
      const clouds = new THREE.Mesh(
        sphere(radius * 1.012),
        new THREE.MeshStandardMaterial({
          map: T.cloudTexture(),
          transparent: true,
          roughness: 1,
          depthWrite: false,
        }),
      );
      body.add(clouds);
      spinners.push({ object: clouds, rate: 0.066 });
      const atmosphere = new THREE.Mesh(
        new THREE.SphereGeometry(radius * 1.06, 64, 48),
        new THREE.MeshBasicMaterial({
          color: 0x4d90ff,
          transparent: true,
          opacity: 0.18,
          side: THREE.BackSide,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      extras.push(atmosphere);
      break;
    }
    case 'mars':
      body = new THREE.Mesh(
        sphere(radius),
        new THREE.MeshStandardMaterial({ map: T.marsTexture(), roughness: 0.95 }),
      );
      break;
    case 'jupiter':
      body = new THREE.Mesh(
        sphere(radius),
        new THREE.MeshStandardMaterial({
          map: T.gasTexture(T.jupiterBands, 51),
          roughness: 0.9,
        }),
      );
      break;
    case 'saturn':
      body = new THREE.Mesh(
        sphere(radius),
        new THREE.MeshStandardMaterial({
          map: T.gasTexture(T.saturnBands, 52, 1024, 512, 0.03),
          roughness: 0.9,
        }),
      );
      break;
    default:
      // An unknown planet still renders, as a plain rocky world, rather than
      // leaving a hole in the lesson.
      body = new THREE.Mesh(
        sphere(radius),
        new THREE.MeshStandardMaterial({ map: T.marsTexture(), roughness: 0.95 }),
      );
  }

  if (object.ring) {
    const [innerFactor, outerFactor] = object.ring;
    const inner = innerFactor * radius;
    const outer = outerFactor * radius;
    const geometry = new THREE.RingGeometry(inner, outer, 128, 1);
    // Remap UVs so the 1-D ring texture runs from the inner edge outwards.
    const position = geometry.attributes['position']!;
    const uv = geometry.attributes['uv']!;
    const v = new THREE.Vector3();
    for (let i = 0; i < position.count; i++) {
      v.fromBufferAttribute(position, i);
      uv.setXY(i, (v.length() - inner) / (outer - inner), 0.5);
    }
    const ring = new THREE.Mesh(
      geometry,
      new THREE.MeshStandardMaterial({
        map: T.ringTexture(),
        transparent: true,
        side: THREE.DoubleSide,
        roughness: 1,
        depthWrite: false,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    body.add(ring);
  }

  if (object.tilt) body.rotation.z = object.tilt;

  const labelHeight = radius * (object.ring ? 2.0 : 1.35) + 0.2;
  return { body, extras, spinners, labelHeight };
});

builders.set('moon', ({ object }) => {
  const radius = object.radius ?? DEFAULT_RADIUS.moon;
  const body = new THREE.Mesh(
    sphere(radius, 0),
    new THREE.MeshStandardMaterial({ map: T.moonTexture(), roughness: 1 }),
  );
  return { body, labelScale: 0.6, labelHeight: radius + 0.15 };
});

builders.set('station', ({ object }) => {
  // Campus-tour stations: a plain marker post. The campus preset is not built
  // yet (docs/04), but a station referenced by a viewpoint must still exist.
  const radius = object.radius ?? DEFAULT_RADIUS.station;
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius, 0.08, 24),
    new THREE.MeshStandardMaterial({ color: 0x3d85c6, roughness: 0.8 }),
  );
  return { body, labelHeight: 0.6 };
});

builders.set('model', ({ name, object, catalogue }) => {
  // A library model (ADR-043): what is seen is its GLB, which render/models/
  // runtime.ts loads into the group and walks, turns and plays. The body is
  // its box at the lesson's size, never drawn: what the pupil points at for a
  // hotspot, turned with the model by the runtime. Without its facts (not in
  // the library any more) nothing at all, and the lesson goes on.
  const facts = catalogue?.[name];
  if (!facts) {
    const body = new THREE.Mesh(
      new THREE.BufferGeometry(),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    return { body, labelHeight: 0.6 };
  }
  const scale = modelScale(object, facts);
  const { width, height, length } = facts.box;
  const geometry = new THREE.BoxGeometry(width * scale, height * scale, length * scale);
  geometry.translate(0, (height * scale) / 2, 0);
  const body = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ visible: false }));
  body.name = 'model-box';
  const top = height * scale;
  return {
    body,
    labelHeight: top + 0.3,
    radius: Math.max(width, length) * scale,
    // Just above its name, by the model's height: the planet rule took its
    // length for a radius and put a T. rex's badge 17 m up (Quest 3, 25 Oct
    // 2026). A big model is seen from further off, so its badge is bigger.
    badge: { at: [0, top + 0.9, 0], size: Math.min(0.9, Math.max(0.2, top * 0.15)) },
  };
});

/**
 * The object namespaces this headset can build. lesson-schema's catalogue is
 * tested against this list, so the lesson generator never offers a model
 * something the headset cannot draw.
 */
export function registeredKinds(): string[] {
  return [...builders.keys()];
}

export type BuiltScene = {
  readonly root: THREE.Group;
  readonly objects: ReadonlyMap<string, BuiltObject>;
  readonly geometry: SceneGeometry;
  readonly lights: readonly THREE.Light[];
  /** How finely this lesson's shapes are drawn, and what they would cost at full detail. */
  readonly shapes: ShapeBudget;
  /** Each poster's photo, by object id. */
  readonly posters: ReadonlyMap<string, PosterController>;
  /**
   * Settles once every poster's photo has arrived or failed; how many failed
   * (a filled poster whose photo could not be had), for the desktop note.
   * Never waited for: a lesson does not wait for a photo.
   */
  readonly photos: Promise<{ failed: number }>;
  dispose(): void;
};

export type SceneOptions = {
  /** Media id -> signed URL, as the lesson came with it. */
  media?: Readonly<Record<string, string>>;
  /** How a poster's photo is fetched; tests replace it. */
  loadPhoto?: (poster: PosterController, url: string) => Promise<boolean>;
  /** The served lesson's library models (ADR-043, contract C4): the model builder's boxes. */
  models?: ModelCatalogue;
  /**
   * The look (ADR-032): `full`, the default, with the one soft shadow and
   * sky light; `flat`, the look before worlds, for `?look=flat` -- a world
   * lesson then stands in the old studio.
   */
  look?: 'full' | 'flat';
  /** The lesson's world as a catalogue (a package's features, or the code world's): where framing finds them. */
  worlds?: WorldCatalogue;
};

/**
 * Builds everything a lesson's `scene` and `objects` describe.
 *
 * An unknown `kind` namespace is skipped with a warning rather than throwing:
 * the schema already rejects malformed lessons before they ship, and a single
 * missing decoration should not end a class.
 */
export function buildScene(lesson: Lesson, options: SceneOptions = {}): BuiltScene {
  const root = new THREE.Group();
  const posters = new Map<string, PosterController>();
  const objects = new Map<string, BuiltObject>();
  const lights: THREE.Light[] = [];
  const isAr = lesson.scene.type === 'ar-room';
  const flat = options.look === 'flat';
  const world = lesson.scene.type === 'world';

  if (isAr) {
    // Passthrough: no environment of our own, just enough light for the
    // objects we place on real surfaces.
    lights.push(new THREE.HemisphereLight(0xffffff, 0x667788, 2.2));
  } else if (world && !flat) {
    // A world brings its own sky, sun and light (render/look/look.ts).
  } else if (lesson.scene.type === 'pano360') {
    // A 360° photo lights the lesson itself (ADR-036), and its view keeps a
    // studio of its own for a photo that cannot be had and for ?look=flat
    // (render/pano.ts, built by render/look/look.ts).
  } else if (lesson.scene.type === 'studio' || world) {
    // Its own light: the space lessons' dim blue ambient would leave a
    // volcano a silhouette against a pale sky.
    const studio = buildStudio({ flat });
    root.add(studio.root);
    lights.push(...studio.lights);
  } else {
    lights.push(new THREE.AmbientLight(0x3a4a70, 0.7));
  }

  if (lesson.scene.type === 'procedural' && lesson.scene.preset === 'solar-system') {
    root.add(starfield());
  }

  let lightSource: string | undefined;

  // Decided for the whole lesson before anything is built: a class sees one
  // level of detail, not the first shapes fine and the rest coarse.
  const shapes = shapeDetailFor(lesson);
  if (shapes.detail === 'simple') {
    const still = shapes.drawn > shapes.limit ? `, still over the budget of ${shapes.limit}` : '';
    console.warn(
      `shapes would take ${shapes.triangles} triangles at full detail; drawing them simpler (${shapes.drawn}${still})`,
    );
  }

  for (const [id, object] of Object.entries(lesson.objects ?? {})) {
    const [namespace, name = ''] = object.kind.split(':') as [string, string?];
    const builder = builders.get(namespace);
    if (!builder) {
      console.warn(`no builder for object kind "${object.kind}" (${id}); skipping`);
      continue;
    }

    const built = builder({
      id,
      name,
      object,
      shapeDetail: shapes.detail,
      ...(options.models ? { catalogue: options.models } : {}),
    });
    const group = new THREE.Group();
    group.add(built.body);
    for (const extra of built.extras ?? []) group.add(extra);

    const label = makeLabel(object.label, built.labelScale ?? 1);
    let badge: BuiltObject['badge'] = built.badge
      ? { at: new THREE.Vector3(...built.badge.at), size: built.badge.size }
      : undefined;
    if (built.nameplate) {
      label.position.set(...built.nameplate.at);
      // Out from the anchor by most of the label's width, so the text starts
      // beside the object instead of on top of it.
      label.position.x += built.nameplate.side * label.scale.x * 0.42;
      badge = {
        at: label.position.clone().add(new THREE.Vector3(0, label.scale.y * 0.85, 0)),
        size: label.scale.y * 0.75,
      };
    } else {
      label.position.y = built.labelHeight ?? (object.radius ?? 0.5) * 1.35 + 0.2;
    }
    group.add(label);

    if (object.pos) group.position.set(...object.pos);
    // As declared, before any step changes it (render/motion.ts plays the rest).
    if (object.rot) {
      const [x, y, z] = object.rot;
      group.rotation.set((x * Math.PI) / 180, (y * Math.PI) / 180, (z * Math.PI) / 180);
    }
    if (object.scale) group.scale.setScalar(object.scale);
    // A poster hangs still, turned by its facing (lesson-schema's
    // posterFacing, which framing reads too).
    if (isPosterKind(object.kind)) group.rotation.y = (posterFacing(lesson, id) * Math.PI) / 180;
    if (object.hidden) group.visible = false;
    if (built.poster) posters.set(id, built.poster);

    built.body.userData['objectId'] = id;
    // The one soft shadow (ADR-032): lesson objects cast it and catch it, in
    // VR; AR has no shadow catcher yet, and a shadow on nothing is noise.
    if (!isAr && !flat) {
      group.traverse((node) => {
        if (node instanceof THREE.Mesh && !(node instanceof THREE.Sprite)) {
          node.castShadow = true;
          node.receiveShadow = true;
        }
      });
    }

    objects.set(id, {
      id,
      group,
      body: built.body,
      label,
      radius: built.radius ?? object.radius ?? 0.5,
      spin: object.spin ?? 0,
      parent: object.parent,
      orbit: object.orbit,
      spinners: built.spinners ?? [],
      badge,
    });
    root.add(group);

    if (namespace === 'star') {
      lightSource ??= id;
      const point = new THREE.PointLight(0xfff1dc, 22, 0, 1.0);
      if (object.pos) point.position.set(...object.pos);
      lights.push(point);
    }
  }

  for (const light of lights) root.add(light);

  // Every poster's photo at once, none waited for. A filled poster whose id
  // the lesson came without a URL for -- removed, or no signer -- says so.
  const load = options.loadPhoto ?? ((poster, url) => loadPosterPhoto(poster, url));
  const photos = Promise.all(
    [...posters].map(async ([id, poster]) => {
      const mediaId = lesson.objects?.[id]?.media;
      if (mediaId === undefined) return true;
      const url = options.media?.[mediaId];
      if (url === undefined) {
        poster.unavailable();
        return false;
      }
      return load(poster, url);
    }),
  ).then((results) => ({ failed: results.filter((ok) => !ok).length }));

  // The library models' facts (ADR-043): framing and the lectern see them as
  // the validator does, each step's states from the same replay (modelStatesAt).
  const catalogue =
    options.models && Object.keys(options.models).length > 0 ? options.models : undefined;
  const statesAt = catalogue
    ? modelStatesAt(lesson, catalogue, undefined, options.worlds)
    : undefined;
  const geometry: SceneGeometry = {
    lightSource,
    // Framing measures the lesson, not the meshes: the API's check does the
    // same from the same document (render/bounds-fit.test.ts keeps them honest).
    framing: framingScene(lesson, undefined, catalogue, undefined, options.worlds),
    framingFor: framingPerStep(lesson, statesAt, catalogue, options.worlds),
    objects: new Map(
      [...objects].map(([id, built]) => {
        const source = lesson.objects?.[id];
        const position: Vec3 = source?.pos ?? worldPositionOf(built, objects);
        const ringOuter = source?.ring ? source.ring[1] * built.radius : undefined;
        return [id, { position, radius: built.radius, ringOuter }];
      }),
    ),
  };

  return {
    root,
    objects,
    geometry,
    lights,
    shapes,
    posters,
    photos,
    dispose(): void {
      for (const poster of posters.values()) poster.dispose();
      // A shadow-casting light holds its shadow map on the GPU until told:
      // the studio's sun would otherwise leave one behind every lesson.
      for (const light of lights) light.dispose();
      root.traverse((node) => {
        // A name is a sprite with a canvas of its own (panel.ts, makeLabel):
        // its material and that texture go, but not its geometry, which
        // every sprite shares and three.js owns.
        if (node instanceof THREE.Sprite) {
          node.material.map?.dispose();
          node.material.dispose();
          return;
        }
        // Lines and points too: the studio's grid, the starfield.
        if (!(
          node instanceof THREE.Mesh ||
          node instanceof THREE.Line ||
          node instanceof THREE.Points
        )) {
          return;
        }
        // Without type arguments geometry and material widen to any, which
        // the type-aware lint rules rightly object to.
        const drawn = node as THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>;
        drawn.geometry.dispose();
        for (const m of Array.isArray(drawn.material) ? drawn.material : [drawn.material]) {
          m.dispose();
        }
      });
    },
  };
}

/**
 * Every body a lesson placed, in the lesson's own space. Call it before
 * anything moves the root: an AR lesson's padlock is placed from this
 * (engine/lock-anchor.ts), in the root's space, and `arrive` then moves both.
 */
export function boundsOf(built: BuiltScene): Bounds {
  built.root.updateMatrixWorld(true);
  const box = new THREE.Box3();
  for (const object of built.objects.values()) box.expandByObject(object.body);
  return box.isEmpty()
    ? { min: [0, 0, 0], max: [0, 0, 0] }
    : { min: box.min.toArray(), max: box.max.toArray() };
}

/**
 * Each step's scene as the step ends, after its shows, hides and moves, so
 * the pupil is placed where the result can be seen; worked out once a step.
 */
function framingPerStep(
  lesson: Lesson,
  statesAt?: (index: number) => ReadonlyMap<string, ObjectState>,
  catalogue?: ModelCatalogue,
  worlds?: WorldCatalogue,
): (step: Step) => FramingScene {
  const scenes = new Map<Step, FramingScene>();
  return (step) => {
    let scene = scenes.get(step);
    if (!scene) {
      const index = lesson.steps.findIndex((candidate) => candidate.id === step.id);
      // As the step ends, with what the lectern must keep clear of during it.
      scene = framingSceneAt(lesson, index, statesAt, catalogue, worlds);
      scenes.set(step, scene);
    }
    return scene;
  };
}

/**
 * Where viewpoint maths puts an object without a `pos`: an orbiting one at
 * the centre of its orbit, its parent's position. Not where the body happens
 * to be -- it moves -- and the same point lesson-schema's framing uses
 * (`ObjectState.pos`), so the headset and the generator check face the same
 * way for a `{pos, look: <moon>}` view.
 */
function worldPositionOf(built: BuiltObject, all: ReadonlyMap<string, BuiltObject>): Vec3 {
  const parent = built.parent ? all.get(built.parent) : undefined;
  if (!parent) return [0, 0, 0];
  const p = parent.group.position;
  return [p.x, p.y, p.z];
}

function starfield(): THREE.Points {
  const count = 4000;
  const positions = new Float32Array(count * 3);
  const colours = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const v = new THREE.Vector3().randomDirection();
    if (i % 3 === 0) v.y *= 0.15; // a faint Milky Way band
    v.multiplyScalar(120 + Math.random() * 60);
    positions.set([v.x, v.y, v.z], i * 3);
    const b = 0.5 + Math.random() * 0.5;
    const warm = Math.random();
    colours.set([b, b * (0.85 + 0.15 * warm), b * (0.8 + 0.2 * (1 - warm))], i * 3);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));
  return new THREE.Points(
    geometry,
    new THREE.PointsMaterial({ size: 0.5, vertexColors: true, sizeAttenuation: true }),
  );
}
